import { forwardRef, useId, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import './help-scroll-area.scss';

const HelpScrollArea = forwardRef<HTMLDivElement, HelpScrollAreaProps>(({
  children, onUserScroll, className = '', contentClassName = '', label, onScroll, persistentScrollbar = false,
}, ref) => {
  const id = useId();
  const [ customScrollbar ] = useState(() => typeof window.PointerEvent !== 'undefined' && typeof window.ResizeObserver !== 'undefined');
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HelpScrollDrag | null>(null);
  const [ geometry, setGeometry ] = useState({ height: 0, top: 0, max: 0, value: 0 });

  useImperativeHandle(ref, () => viewportRef.current as HTMLDivElement);

  const update = () => {
    const viewport = viewportRef.current;
    const track = trackRef.current;

    if (!viewport || !track) return;

    const max = viewport.scrollHeight - viewport.clientHeight;
    const height = max > 0 ? Math.min(track.clientHeight, Math.max(24,
      track.clientHeight * viewport.clientHeight / viewport.scrollHeight)) : Math.min(80, track.clientHeight);
    const top = max > 0 ? viewport.scrollTop / max * (track.clientHeight - height) : 0;
    const next = { height, top, max, value: viewport.scrollTop };

    setGeometry(previous => {
      const unchanged = Object.keys(next).every(key => previous[key as keyof typeof next] === next[key as keyof typeof next]);

      return unchanged ? previous : next;
    });
  };

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;

    if (!viewport || !content) return;

    const observer = customScrollbar ? new ResizeObserver(update) : null;
    observer?.observe(viewport);
    observer?.observe(content);
    viewport.addEventListener('scroll', update);
    update();

    return () => {
      observer?.disconnect();
      viewport.removeEventListener('scroll', update);
    };
  }, []);

  useLayoutEffect(() => {
    const track = trackRef.current;
    const viewport = viewportRef.current;

    if (!track || !viewport) return;

    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

      let multiplier = 1;

      if (event.deltaMode === 1) multiplier = 18;
      if (event.deltaMode === 2) multiplier = viewport.clientHeight;

      const previous = viewport.scrollTop;
      viewport.scrollTop += event.deltaY * multiplier;
      onUserScroll?.(viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 5);

      if (viewport.scrollTop !== previous) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    track.addEventListener('wheel', handleWheel, { passive: false });

    return () => track.removeEventListener('wheel', handleWheel);
  }, [ onUserScroll ]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current;
    const track = trackRef.current;

    if (!viewport || !track || geometry.max <= 0) return;

    if (event.button !== 0 || !event.isPrimary) return;

    event.preventDefault();
    event.currentTarget.focus({ preventScroll: true });
    onUserScroll?.();
    event.currentTarget.setPointerCapture(event.pointerId);
    const bounds = track.getBoundingClientRect();
    const scale = bounds.height / track.clientHeight;
    const y = (event.clientY - bounds.top) / scale;
    const onThumb = y >= geometry.top && y <= geometry.top + geometry.height;

    if (!onThumb) {
      viewport.scrollTop = (y - geometry.height / 2) / (track.clientHeight - geometry.height) * geometry.max;
      onUserScroll?.(viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 5);
    }

    dragRef.current = { y: event.clientY, scrollTop: viewport.scrollTop, scale };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    const drag = dragRef.current;

    if (!viewport || !track || !drag) return;

    const distance = (event.clientY - drag.y) / drag.scale;
    viewport.scrollTop = drag.scrollTop + distance / (track.clientHeight - geometry.height) * geometry.max;
    onUserScroll?.(viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 5);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current;

    if (!viewport) return;

    const distances: Record<string, number> = {
      ArrowDown: 40,
      ArrowUp: -40,
      PageDown: viewport.clientHeight,
      PageUp: -viewport.clientHeight,
      Home: -viewport.scrollHeight,
      End: viewport.scrollHeight,
    };
    const distance = distances[event.key];

    if (distance === undefined) return;

    event.preventDefault();
    viewport.scrollTop += distance;
    onUserScroll?.(viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 5);
  };

  return <div className={`help-scroll-area ${className}${customScrollbar ? '' : ' help-scroll-area_native'}`}>
    <div id={id} ref={viewportRef} className='help-scroll-area__viewport' tabIndex={0} aria-label={label} onScroll={onScroll} onKeyDown={(event) => {
      const scrollingKeys = [ 'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ' ];

      if (scrollingKeys.includes(event.key)) onUserScroll?.();
    }}>
      <div ref={contentRef} className={`help-scroll-area__content ${contentClassName}`}>{children}</div>
    </div>
    <div
      ref={trackRef}
      className='help-scroll-area__track'
      role='scrollbar'
      aria-label={label}
      aria-controls={id}
      aria-orientation='vertical'
      aria-hidden={geometry.max <= 0}
      aria-valuemin={0}
      aria-valuemax={Math.max(0, Math.round(geometry.max))}
      aria-valuenow={Math.round(geometry.value)}
      tabIndex={geometry.max > 0 ? 0 : -1}
      style={{
        visibility: geometry.max > 0 || persistentScrollbar ? undefined : 'hidden',
        pointerEvents: geometry.max > 0 ? undefined : 'none',
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={() => { dragRef.current = null; }}
      onPointerCancel={() => { dragRef.current = null; }}
      onLostPointerCapture={() => { dragRef.current = null; }}
      onKeyDown={handleKeyDown}
    >
      <div className='help-scroll-area__thumb' style={{ height: geometry.height, transform: `translateY(${geometry.top}px)` }} />
    </div>
  </div>;
});

HelpScrollArea.displayName = 'HelpScrollArea';

export default HelpScrollArea;
